import { useState, useEffect } from 'react';
import MarkdownRenderer from './MarkdownRenderer';

export default function MarkdownEditor({
  content,
  parameters = {},
  onSave,
  onChange = null, // Called on every content change (useful with externalControls)
  readOnly = false,
  className = '',
  textareaClassName = 'h-[500px]',
  placeholder = 'Enter markdown content with parameters like {{param1}}...',
  showBorder = false,
  maxHeight = null,
  externalControls = false, // If true, hide internal edit/save/cancel buttons
  isEditing: externalIsEditing = null, // External editing state
  onEdit = null, // External edit handler
  fontSize = 'text-sm' // Tailwind font size class (e.g., 'text-xs', 'text-sm', 'text-base', 'text-lg')
}) {
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(content);

  // Use external or internal editing state
  const isEditing = externalControls ? externalIsEditing : internalIsEditing;

  // Sync editedContent when content prop changes
  useEffect(() => {
    setEditedContent(content);
  }, [content]);

  const handleEdit = () => {
    setEditedContent(content);
    if (externalControls && onEdit) {
      onEdit();
    } else {
      setInternalIsEditing(true);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(editedContent);
    }
    if (!externalControls) {
      setInternalIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditedContent(content);
    if (!externalControls) {
      setInternalIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className={`relative h-full ${className}`}>
        <textarea
          value={editedContent}
          onChange={(e) => {
            setEditedContent(e.target.value);
            // Call onChange immediately if provided (useful with externalControls)
            if (onChange) {
              onChange(e.target.value);
            }
          }}
          className={`w-full px-4 py-3 ${externalControls ? '' : 'pb-12'} bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 resize-none ${fontSize} ${textareaClassName}`}
          placeholder={placeholder}
          style={maxHeight ? { maxHeight } : {}}
        />
        {/* Overlay buttons - Bottom Right (only if not using external controls) */}
        {!externalControls && (
          <div className="absolute bottom-3 right-3 flex gap-2">
            <button
              onClick={handleCancel}
              className="p-2 bg-gray-700/20 hover:bg-gray-700/80 rounded transition-all duration-200 opacity-30 hover:opacity-100"
              style={{ color: 'var(--color-text-primary)' }}
              title="Cancel"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <button
              onClick={handleSave}
              className="p-2 bg-purple-600/20 hover:bg-purple-600/80 rounded transition-all duration-200 opacity-30 hover:opacity-100"
              style={{ color: 'var(--color-text-on-accent)' }}
              title="Save"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`group relative flex flex-col h-full ${showBorder ? 'bg-gray-900 border border-gray-700 rounded' : ''} ${className}`}
      style={maxHeight ? { maxHeight } : {}}
    >
      {/* Rendered Markdown - Takes available space */}
      <div
        className={`flex-1 min-h-0 overflow-auto ${showBorder ? 'px-3 py-2' : ''} focus:outline-none focus:ring-2 focus:ring-purple-500/50 rounded`}
        tabIndex={0}
        onKeyDown={(e) => {
          // Handle Ctrl+A to select all text within this container
          if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
            e.preventDefault();
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(e.currentTarget);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }}
      >
        <MarkdownRenderer content={content} parameters={parameters} fontSize={fontSize} />
      </div>

      {/* Edit Button - Overlay Bottom Right (only if not using external controls) */}
      {!readOnly && !externalControls && (
        <button
          onClick={handleEdit}
          className="absolute bottom-2 right-2 p-2 bg-purple-600/20 hover:bg-purple-600/80 rounded transition-all duration-200 opacity-0 group-hover:opacity-30 hover:!opacity-100 pointer-events-auto"
          style={{ color: 'var(--color-text-primary)' }}
          title="Edit"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      )}
    </div>
  );
}
